import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useId, useState } from 'react';
import { ApiError, errorMessage } from '../api/client';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  busy?: boolean;
};

export function Button({ variant = 'primary', busy = false, disabled, children, className = '', ...rest }: ButtonProps) {
  return (
    <button className={`btn btn--${variant} ${className}`} disabled={disabled || busy} aria-busy={busy} {...rest}>
      {busy ? <span className="spinner spinner--small" aria-hidden="true" /> : null}
      <span>{children}</span>
    </button>
  );
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string };

/** Contraseña con el «ojito» para ver lo que se escribe. */
export function PasswordField({ label, hint, ...input }: Omit<FieldProps, 'type'>) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="password-input">
        <input id={id} type={visible ? 'text' : 'password'} aria-describedby={hint ? `${id}-hint` : undefined} {...input} />
        <button
          type="button"
          className="password-input__eye"
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          aria-pressed={visible}
          onClick={() => setVisible((v) => !v)}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
            <circle cx="12" cy="12" r="3" />
            {visible ? null : <path d="M3 3l18 18" />}
          </svg>
        </button>
      </div>
      {hint ? (
        <small id={`${id}-hint`} className="field__hint">
          {hint}
        </small>
      ) : null}
    </div>
  );
}

export function Field({ label, hint, ...input }: FieldProps) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} aria-describedby={hint ? `${id}-hint` : undefined} {...input} />
      {hint ? (
        <small id={`${id}-hint`} className="field__hint">
          {hint}
        </small>
      ) : null}
    </div>
  );
}

export function Card({ title, action, children }: { title?: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="card">
      {title || action ? (
        <header className="card__header">
          {title ? <h2 className="card__title">{title}</h2> : <span />}
          {action}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function Loading({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="state state--empty">
      <p className="state__title">{title}</p>
      {children}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="state state--error" role="alert">
      <p className="state__title">{errorMessage(error)}</p>
      {/* Sin permiso o sin la denominación verificada: reintentar no cambia nada. */}
      {onRetry && !(error instanceof ApiError && (error.status === 403 || error.status === 404)) ? (
        <Button variant="secondary" onClick={onRetry}>
          Reintentar
        </Button>
      ) : null}
    </div>
  );
}

export function Alert({ tone = 'error', children }: { tone?: 'error' | 'success' | 'info' | 'warning'; children: ReactNode }) {
  return (
    <div className={`alert alert--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

/** Hoja K: cruz de la que brotan dos hojas (docs/marca). */
export function Logo() {
  return (
    <span className="logo" role="img" aria-label="KEORA">
      <svg className="logo__mark" viewBox="29 11 57 83" width="22" height="32" aria-hidden="true">
        <path d="M44 13V92M31 34H57" stroke="var(--brand-cross)" strokeWidth="5" fill="none" />
        <path d="M50 56Q58 30 84 24Q78 50 50 56Z" fill="var(--brand-leaf)" />
        <path d="M50 56Q78 62 84 90Q58 86 50 56Z" fill="var(--brand-leaf)" fillOpacity="0.65" />
      </svg>
      <svg className="logo__text" viewBox="0 10 110 31" width="78" height="22" aria-hidden="true">
        <path d="M0.9 40Q0.8 40 0.8 39.8Q0.8 39.5 0.9 39.5Q2.3 39.5 2.7 39Q3.2 38.4 3.2 36.8V16.3Q3.2 14.9 2.9 14.3Q2.7 13.7 2 13.7Q1.4 13.7 0.4 14.2Q0.2 14.2 0.1 14Q0 13.7 0.1 13.7L5.7 11Q5.8 11 5.9 11Q6 11 6.2 11.2Q6.4 11.3 6.4 11.5V36.8Q6.4 38.4 6.9 39Q7.3 39.5 8.7 39.5Q8.8 39.5 8.8 39.8Q8.8 40 8.7 40Q7.9 40 6.9 40Q5.9 39.9 4.8 39.9Q3.7 39.9 2.7 40Q1.7 40 0.9 40ZM11 40Q10.9 40 10.9 39.8Q10.9 39.5 11 39.5Q12.2 39.5 12.5 39.1Q12.8 38.6 12.3 37.9L7.4 31.4L9.8 29.6L15.2 36.8Q16.4 38.3 17.5 38.9Q18.6 39.5 19.7 39.5Q19.8 39.5 19.8 39.8Q19.8 40 19.7 40Q18.9 40 17.9 40Q16.9 39.9 15.8 39.9Q14.2 39.9 13.1 40Q11.9 40 11 40ZM5.2 33.8 5 33.2 10.7 28.1Q12 27 11.8 26Q11.6 25 10 25Q9.9 25 9.9 24.8Q9.9 24.6 10 24.6Q11 24.6 12.1 24.6Q13.1 24.6 14.9 24.6Q16.4 24.6 17.4 24.6Q18.4 24.6 19.2 24.6Q19.3 24.6 19.3 24.8Q19.3 25 19.2 25Q18.2 25 16.9 25.4Q15.7 25.7 14.3 26.4Q12.9 27.1 11.6 28.2Z M34.4 40.5Q31.9 40.5 30.1 39.4Q28.4 38.3 27.5 36.5Q26.6 34.8 26.6 32.7Q26.6 30.4 27.7 28.4Q28.9 26.5 30.9 25.4Q32.9 24.2 35.4 24.2Q37.7 24.2 39.1 25.3Q40.4 26.4 40.4 28.8Q40.4 29.3 40.3 29.6Q40.2 30 40 30H36.7Q36.8 27.6 36.1 26.3Q35.5 25 34 25Q32.2 25 31.2 26.7Q30.1 28.3 30.1 31.1Q30.1 33.4 30.9 35.1Q31.6 36.8 33 37.8Q34.4 38.7 36.4 38.7Q37.7 38.7 38.5 38.4Q39.3 38 40.3 37.5Q40.4 37.4 40.5 37.6Q40.7 37.8 40.6 37.9Q39.1 39.3 37.6 39.9Q36.1 40.5 34.4 40.5ZM29 30 28.9 29.3 37.8 29.2V30Z M56.8 40.5Q54.3 40.5 52.5 39.4Q50.7 38.2 49.7 36.3Q48.7 34.3 48.7 32.1Q48.7 30 49.5 28.5Q50.3 27 51.6 26Q52.9 25 54.5 24.5Q56 24 57.5 24Q60 24 61.8 25.2Q63.6 26.4 64.5 28.3Q65.5 30.2 65.5 32.3Q65.5 34.8 64.3 36.6Q63.1 38.5 61.1 39.5Q59.1 40.5 56.8 40.5ZM58 39.7Q59.8 39.7 60.7 38.2Q61.7 36.6 61.7 33.6Q61.7 30.8 60.9 28.9Q60.1 26.9 58.9 25.9Q57.6 24.9 56.1 24.9Q54.4 24.9 53.4 26.3Q52.5 27.8 52.5 30.7Q52.5 33.3 53.2 35.4Q53.9 37.4 55.2 38.6Q56.4 39.7 58 39.7Z M78.6 29.2 78.4 28.7Q79.9 26.9 80.9 25.9Q82 24.9 82.7 24.6Q83.5 24.2 84.1 24.2Q84.9 24.2 85.8 24.7Q86.7 25.3 86.7 26.2Q86.7 26.9 86.2 27.4Q85.8 27.9 85 27.9Q84.2 27.9 83.8 27.5Q83.4 27.1 83.1 26.7Q82.7 26.3 82.1 26.3Q81.8 26.3 81.4 26.5Q81 26.7 80.4 27.3Q79.8 27.9 78.6 29.2ZM74.1 40Q74 40 74 39.8Q74 39.5 74.1 39.5Q75.4 39.5 75.9 39Q76.3 38.4 76.3 36.8V29.1Q76.3 27.8 76 27.2Q75.7 26.7 75 26.7Q74.7 26.7 74.2 26.8Q73.7 26.9 73 27.2Q72.9 27.3 72.8 27.1Q72.7 26.8 72.8 26.8L78 24.2Q78.2 24.2 78.3 24.2Q78.7 24.2 79.1 25.1Q79.6 26 79.6 27.7V36.8Q79.6 37.8 79.9 38.4Q80.2 39.1 81 39.3Q81.8 39.5 83.3 39.5Q83.4 39.5 83.4 39.8Q83.4 40 83.3 40Q82.2 40 80.9 40Q79.5 39.9 78 39.9Q76.9 39.9 75.9 40Q74.8 40 74.1 40Z M105.7 40.2Q105.5 40.3 105.3 40.3Q104.7 40.3 104 39.4Q103.4 38.5 103.4 36.7V29.7Q103.4 27.2 102.9 26Q102.4 24.9 101.3 24.9Q100.3 24.9 99.8 25.5Q99.2 26.1 99 26.8Q98.8 27.5 98.7 28.1Q98.6 29.1 98.1 29.6Q97.6 30.1 96.7 30.1Q95.8 30.1 95.4 29.7Q95 29.3 95 28.7Q95 27.8 95.8 27Q96.6 26.2 97.9 25.6Q99.1 24.9 100.5 24.5Q101.8 24.2 102.9 24.2Q104.5 24.2 105.6 25.2Q106.6 26.3 106.6 28.5V35.7Q106.6 36.9 107 37.6Q107.4 38.2 108.2 38.2Q108.8 38.2 109.7 37.8Q109.8 37.8 109.9 38Q110 38.2 109.8 38.3ZM98.2 40.3Q96.5 40.3 95.7 39.5Q94.8 38.7 94.8 37.4Q94.8 36.3 95.4 35.6Q96.1 34.9 97.1 34.5Q98.2 34 99.5 33.5L103.8 31.9L103.9 32.5L101 33.8Q100.5 33.9 100 34.2Q99.4 34.5 99 35Q98.6 35.5 98.6 36.3Q98.6 37.3 99.3 37.8Q99.9 38.4 100.7 38.4Q101.1 38.4 101.4 38.3Q101.8 38.2 102.3 37.9L104.4 36.5L104.5 37.1L101.6 39Q100.7 39.7 99.9 40Q99.2 40.3 98.2 40.3Z" fill="currentColor" />
      </svg>
    </span>
  );
}
